// Temperature units data for conversion routes
// Temperature uses formulas instead of simple multiplication factors

export const temperatureUnits = [
  { id: 'celsius', name: 'Celsius', symbol: '°C', plural: 'Celsius' },
  { id: 'fahrenheit', name: 'Fahrenheit', symbol: '°F', plural: 'Fahrenheit' },
  { id: 'kelvin', name: 'Kelvin', symbol: 'K', plural: 'Kelvin' },
  { id: 'rankine', name: 'Rankine', symbol: '°R', plural: 'Rankine' },
  { id: 'reaumur', name: 'Réaumur', symbol: '°Ré', plural: 'Réaumur' },
];

// Popular conversions matching common usage
export const popularTemperatureConversions = [
  { from: 'celsius', to: 'fahrenheit' },
  { from: 'fahrenheit', to: 'celsius' },
  { from: 'celsius', to: 'kelvin' },
  { from: 'kelvin', to: 'celsius' },
  { from: 'fahrenheit', to: 'kelvin' },
  { from: 'kelvin', to: 'fahrenheit' },
  { from: 'celsius', to: 'rankine' },
  { from: 'rankine', to: 'celsius' },
  { from: 'fahrenheit', to: 'rankine' },
  { from: 'rankine', to: 'fahrenheit' },
  { from: 'kelvin', to: 'rankine' },
  { from: 'rankine', to: 'kelvin' },
  { from: 'celsius', to: 'reaumur' },
  { from: 'reaumur', to: 'celsius' },
  { from: 'fahrenheit', to: 'reaumur' },
  { from: 'reaumur', to: 'fahrenheit' },
];

// Get unit by ID
export const getUnitById = (id) => {
  return temperatureUnits.find(unit => unit.id === id);
};

// Convert to Celsius (base unit for internal conversion)
const toCelsius = (value, fromUnit) => {
  switch (fromUnit) {
    case 'celsius':
      return value;
    case 'fahrenheit':
      return (value - 32) * 5 / 9;
    case 'kelvin':
      return value - 273.15;
    case 'rankine':
      return (value - 491.67) * 5 / 9;
    case 'reaumur':
      return value * 5 / 4;
    default:
      return null;
  }
};

// Convert from Celsius to target unit
const fromCelsius = (celsius, toUnit) => {
  switch (toUnit) {
    case 'celsius':
      return celsius;
    case 'fahrenheit':
      return celsius * 9 / 5 + 32;
    case 'kelvin':
      return celsius + 273.15;
    case 'rankine':
      return (celsius + 273.15) * 9 / 5;
    case 'reaumur':
      return celsius * 4 / 5;
    default:
      return null;
  }
};

// Convert between temperature units
export const convertTemperature = (value, fromId, toId) => {
  const fromUnit = getUnitById(fromId);
  const toUnit = getUnitById(toId);
  if (!fromUnit || !toUnit) return null;

  const celsius = toCelsius(value, fromId);
  if (celsius === null) return null;
  return fromCelsius(celsius, toId);
};

// Format conversion slug to readable text
export const formatConversionTitle = (fromUnit, toUnit) => {
  return `Convert ${fromUnit.name} to ${toUnit.name}`;
};

// Generate SEO description
export const generateSEODescription = (fromUnit, toUnit) => {
  const sampleValue = 100;
  const result = convertTemperature(sampleValue, fromUnit.id, toUnit.id);
  const formattedResult = parseFloat(result.toPrecision(6)).toString();

  return `Convert ${fromUnit.name} to ${toUnit.name}. ${sampleValue}${fromUnit.symbol} = ${formattedResult}${toUnit.symbol}. Free online temperature converter.`;
};

// Get conversion formula text
export const getConversionFormula = (fromId, toId) => {
  const formulas = {
    'celsius-fahrenheit': '°F = °C × 9/5 + 32',
    'fahrenheit-celsius': '°C = (°F - 32) × 5/9',
    'celsius-kelvin': 'K = °C + 273.15',
    'kelvin-celsius': '°C = K - 273.15',
    'fahrenheit-kelvin': 'K = (°F - 32) × 5/9 + 273.15',
    'kelvin-fahrenheit': '°F = (K - 273.15) × 9/5 + 32',
    'celsius-rankine': '°R = (°C + 273.15) × 9/5',
    'rankine-celsius': '°C = (°R - 491.67) × 5/9',
    'fahrenheit-rankine': '°R = °F + 459.67',
    'rankine-fahrenheit': '°F = °R - 459.67',
    'kelvin-rankine': '°R = K × 9/5',
    'rankine-kelvin': 'K = °R × 5/9',
    'celsius-reaumur': '°Ré = °C × 4/5',
    'reaumur-celsius': '°C = °Ré × 5/4',
    'fahrenheit-reaumur': '°Ré = (°F - 32) × 4/9',
    'reaumur-fahrenheit': '°F = °Ré × 9/4 + 32',
    'kelvin-reaumur': '°Ré = (K - 273.15) × 4/5',
    'reaumur-kelvin': 'K = °Ré × 5/4 + 273.15',
    'rankine-reaumur': '°Ré = (°R - 491.67) × 4/9',
    'reaumur-rankine': '°R = °Ré × 9/4 + 491.67',
  };

  return formulas[`${fromId}-${toId}`] || `${fromId} to ${toId}`;
};
